import CDialBase from './Dials'
import { PlaceInNestedAkixiNameSpace } from '../utils/Utils'
import { EaseInOutQuad } from '../../EasingFunctions'

/**
 * This class draws a new line statistic graphic.
 * @param {object} pNode The DOM element to attach the dial canvas to.
 */
class CGlideSlope extends CDialBase {
    /**
     * Public method.
     *
     * Draws the dial on the canvas.
     *
     * @param {number} [flStopValue] Optional - The value at which the dial ends,
     * defaults to the current dial value.
     * @param {number} [flStartValue] Optional - The value at which the dial
     * starts, defaults to the stop value.
     * @param {number} [flFractionComplete=1.0] Optional - How far through the
     * animation we are, defaults to 100%.
     */
    protected Draw(
        flStopValue: number = this.m_flCurrentValue,
        flStartValue: number = flStopValue,
        flFractionComplete = 1.0
    ): void {
        const p2DContext = this.m_pCanvas.getContext('2d') as CanvasRenderingContext2D

        // Fill in the background
        p2DContext.lineCap = 'butt'
        p2DContext.fillStyle = this.m_sBackgroundColour
        p2DContext.fillRect(0, 0, this.m_iWidth, this.m_iHeight)

        const flMaxFillRatio = 0.75
        const flAspectRatio = 0.5
        let flFontSize = Math.min(this.m_iHeight, this.m_iWidth) * 0.2
        const flMaxHeight = this.m_iWidth * flAspectRatio
        const flActualHeight = Math.min(this.m_iHeight, flMaxHeight)
        const flSize = Math.min(flMaxFillRatio * this.m_iWidth, flActualHeight / flAspectRatio)
        const flLeftEdge = this.m_iWidth / 2 - flSize / 2
        const flRightEdge = this.m_iWidth - flLeftEdge
        const flGaugeWidth = flRightEdge - flLeftEdge
        const flQuarterLine = flLeftEdge + flGaugeWidth / 4
        const flHalfLine = this.m_iWidth / 2
        const flThreeQuarterLine = flRightEdge - flGaugeWidth / 4
        const flMidHeight = (3 * this.m_iHeight) / 5
        const flLineWidth = flGaugeWidth / 80
        const flCrossBarHeight = flGaugeWidth / 10
        const flHalfCrossBarHeight = flCrossBarHeight / 2
        const flProgressLineWidth = flLineWidth * 1.5

        /*
         * Draw the outer dial elements
         */
        p2DContext.strokeStyle = this.m_sForegroundColour
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.lineWidth = flLineWidth
        p2DContext.beginPath()
        p2DContext.moveTo(flLeftEdge, flMidHeight)
        p2DContext.lineTo(flRightEdge, flMidHeight)
        p2DContext.stroke()

        // Draw the white graph lines.
        //
        p2DContext.beginPath()
        //
        p2DContext.moveTo(flLeftEdge, flMidHeight - flHalfCrossBarHeight)
        p2DContext.lineTo(flLeftEdge, flMidHeight + flHalfCrossBarHeight)
        p2DContext.stroke()
        //
        p2DContext.moveTo(flQuarterLine, flMidHeight - flHalfCrossBarHeight)
        p2DContext.lineTo(flQuarterLine, flMidHeight + flHalfCrossBarHeight)
        p2DContext.stroke()
        //
        p2DContext.moveTo(flHalfLine, flMidHeight - 2 * flHalfCrossBarHeight)
        p2DContext.lineTo(flHalfLine, flMidHeight + 2 * flHalfCrossBarHeight)
        p2DContext.stroke()
        //
        p2DContext.moveTo(flThreeQuarterLine, flMidHeight - flHalfCrossBarHeight)
        p2DContext.lineTo(flThreeQuarterLine, flMidHeight + flHalfCrossBarHeight)
        p2DContext.stroke()
        //
        p2DContext.moveTo(flRightEdge, flMidHeight - flHalfCrossBarHeight)
        p2DContext.lineTo(flRightEdge, flMidHeight + flHalfCrossBarHeight)
        p2DContext.stroke()

        /*
         * Draw the centre text.
         */
        const flDisplayValue = EaseInOutQuad(flFractionComplete) * (flStopValue - flStartValue) + flStartValue
        let flUsableFraction = flDisplayValue / this.m_flRange
        if (this.m_bReverseDirection) {
            // Reverse the filling fraction when reversing the dial direction.
            flUsableFraction = 1.0 - flUsableFraction
        }
        const flEndPosition = flUsableFraction * flGaugeWidth
        //
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.textAlign = 'center'
        p2DContext.textBaseline = 'bottom'
        const pTextDimensions = p2DContext.measureText(this.m_sDisplayValue)
        const flWidthRatio = (this.m_iWidth * 0.75) / pTextDimensions.width
        const flHeightRatio = (this.m_iHeight * 0.2) / (parseInt(p2DContext.font, 10) * 1.2)
        flFontSize *= Math.min(flHeightRatio, flWidthRatio)
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.fillText(this.m_sDisplayValue, this.m_iWidth / 2, flMidHeight / 2)

        // Draw the progress bar.
        //
        //
        p2DContext.beginPath()
        p2DContext.lineWidth = flProgressLineWidth
        p2DContext.lineCap = 'round'
        p2DContext.strokeStyle = 'red'
        //
        p2DContext.moveTo(flEndPosition + flLeftEdge, flMidHeight - 2 * flHalfCrossBarHeight)
        p2DContext.lineTo(flEndPosition + flLeftEdge, flMidHeight + 2 * flHalfCrossBarHeight)
        p2DContext.stroke()
    }
}

export default CGlideSlope
PlaceInNestedAkixiNameSpace('Dials', 'CGlideSlope', CGlideSlope)
